// ----------------------------------------------------------------
// Imports
//------------------------------------------------------------------

import {
  reactComponentIds,
  setReactComponent,
  updateReactComponent,
} from "./react";
import Swiper from "swiper";
import { Autoplay } from "swiper/modules"
import { Header } from "./components/header";
import { TopNavigation } from "./components/TopNavigation";
import { Teaser } from "./components/Teaser";
import { LinkBlock } from "./components/linkBlock";
import { CampaignBlock } from "./components/campaignBlock";
import { ExpandableBlock } from "./components/expandableBlock";
import { QuickFacts } from "./components/quickFacts";
import { ValueProposition } from "./components/valueProposition";
import { CampaignHeroLogoSpinner } from "./components/campaignPage/campaignHeroLogoSpinner";
import { CampaignStatisticsBlock } from "./components/campaignStatisticsBlock";
import { CampaignVideoBlock } from "./components/campaignVideoBlock";
import { HamburgerMenu } from "./components/hamburgerMenu";

import "rsuite/dist/rsuite-no-reset.min.css";
import "normalize.css";
import "mapbox-gl/dist/mapbox-gl.css";
import "react-select-search/style.css";
import "swiper/css";
import "../Styles/site.scss";

import { ContainerQueriesPolyfill } from "./components/containerQueryPolyfill";
import { checkDeviceType, devices } from "./utils/checkDeviceType";
import { CampaignInformationSectionBlock } from "./components/campaignInformationSectionBlock";

// Exports
export type SwiperElementType = { element: Element; swiper: Swiper };

export type MapPropsType = {
  show: boolean;
};

export const MapProps: MapPropsType = {
  show: false,
};

type GHABType = {
  showHeroMap: boolean;
};

// Set global variables
declare global {
  interface Window {
    ghab: any;
    onYouTubeIframeAPIReady: () => void;
  }
}
// ---------------------------------------------------------------
//  Elements
// ---------------------------------------------------------------

const topNavigation = document.getElementById("top-navigation");
const hero = document.getElementById("hero") as HTMLElement;
const mapButton = hero?.querySelector(".btn-map") as HTMLElement;
const mapContainer = document.getElementById("mapContainer");
const topBanner = document.querySelector(".top-banner") as Element;
const pageContent = document.getElementById("page-content") as HTMLElement;

// ---------------------------------------------------------------
//  Components
// ---------------------------------------------------------------

let containerQueriesPolyFill: ContainerQueriesPolyfill;
let header: Header;
let topNav: TopNavigation;
let teaser: Teaser;
let linkBlock: LinkBlock;
let campaignBlock: CampaignBlock;
let quickFacts: QuickFacts;
let valueProposition: ValueProposition;
let campaignHeroLogoSpinner: CampaignHeroLogoSpinner;
let campaignStatistics: CampaignStatisticsBlock;
let campaignInformationSection: CampaignInformationSectionBlock;
let campaignVideoBlock: CampaignVideoBlock;
let hamburgerMenu: HamburgerMenu;
let topNavigationHeight: number | undefined;

// ---------------------------------------------------------------
//  Main class
// ---------------------------------------------------------------

export class GHAB {
  private swiperElements: Element[] = [];
  private swiperElementsData: SwiperElementType[] = [];

  // ---------------------------------------------------------------
  //  Constructor
  // ---------------------------------------------------------------

  constructor() {
    // creates component instaces
    header = new Header();
    topNav = new TopNavigation();
    teaser = new Teaser();
    linkBlock = new LinkBlock();
    campaignBlock = new CampaignBlock();
    quickFacts = new QuickFacts();
    valueProposition = new ValueProposition();
    campaignHeroLogoSpinner = new CampaignHeroLogoSpinner();
    campaignStatistics = new CampaignStatisticsBlock();
    campaignInformationSection = new CampaignInformationSectionBlock();
    campaignVideoBlock = new CampaignVideoBlock();
    hamburgerMenu = new HamburgerMenu();

    // init methods
    this.setMap();
    this.setTopBanner();
    this.setSwiper();
    this.setScrollTrigger();
    this.setResizeObserver();
    this.setExpandableBlock();

    const jsElements = document.querySelectorAll(".no-js");
    jsElements.forEach((jsElement) => {
      jsElement.classList.remove("no-js");
    });

    // Starts cruise calls react component if the page has a cruise calls container element
    if (document.getElementById(reactComponentIds.ARRIVALS_DEPARTURES_TEASER)) {
      setReactComponent(reactComponentIds.ARRIVALS_DEPARTURES_TEASER);
    }

    // Starts cruise calls react component if the page has a cruise calls container element
    if (document.getElementById(reactComponentIds.ARRIVALS_DEPARTURES)) {
      setReactComponent(reactComponentIds.ARRIVALS_DEPARTURES);
    }

    // Starts cruise calls react component if the page has a cruise calls container element
    if (document.getElementById(reactComponentIds.CRUSE_CALLS)) {
      setReactComponent(reactComponentIds.CRUSE_CALLS);
    }

    // Starts liner service react component if the page has a liner service container element
    if (document.getElementById(reactComponentIds.LINER_SERVICE_ADMIN)) {
      setReactComponent(reactComponentIds.LINER_SERVICE_ADMIN);
    }

    // Starts cruise calls react component if the page has a cruise calls container element
    if (document.getElementById(reactComponentIds.WEATHER)) {
      setReactComponent(reactComponentIds.WEATHER);
    }

    // Set the articles component if the container ID exists
    if (document.getElementById(reactComponentIds.ARTICLES_WRAPPER)) {
      setReactComponent(reactComponentIds.ARTICLES_WRAPPER);
    }

    // Start the campaign links react component if the container element is present
    if (document.getElementById(reactComponentIds.CAMPAIGN_LINKS_WRAPPER)) {
      setReactComponent(reactComponentIds.CAMPAIGN_LINKS_WRAPPER);
    }

    // Start testimonials react component if the container element is present
    if (
      document.getElementById(reactComponentIds.CAMPAIGN_TESTIMONIALS_SLIDER)
    ) {
      setReactComponent(reactComponentIds.CAMPAIGN_TESTIMONIALS_SLIDER);
    }

    // Start pressrelease archive react component if the container element is present
    if (document.getElementById(reactComponentIds.PRESSRELEASES_WRAPPER)) {
      setReactComponent(reactComponentIds.PRESSRELEASES_WRAPPER);
    }

    // Adds polyfill if container queries is not supported
    if (!CSS.supports("container-type", "inline-size")) {
      containerQueriesPolyFill = new ContainerQueriesPolyfill();
    }
  }

  // ------------------------------------------------------------------
  //  setTopBanner - adds some classes to the topbanner if present
  // ------------------------------------------------------------------

  setTopBanner() {
    const topBannerRow = topBanner.querySelector(".row");

    if (topBannerRow) {
      topBannerRow.classList.add("full-width");
      topBannerRow.classList.add("no-gap");
    }
  }

  // ------------------------------------------------------------------
  //  setSwiper - adds Swiper instance to elements with .swiper class
  // ------------------------------------------------------------------

  setSwiper() {
    this.swiperElements = [...document.querySelectorAll(".swiper")];
    this.swiperElementsData = [];

    this.swiperElements.forEach((element: Element) => {
      const id = new Date().getTime();
      if (!element.id) {
        element.id = `swiper-${id}`;
      }

      const swiper = new Swiper(element as HTMLElement, {
        modules: [Autoplay],
        slidesPerView: "auto",
        slidesOffsetBefore: -30,
        slidesOffsetAfter: 230,
        autoplay: {
          delay: 3000,
          disableOnInteraction: true,
        },
      });

      swiper.autoplay.stop();

      this.swiperElementsData.push({ element, swiper });
    });
  }

  // ------------------------------------------------------------------
  //  setMap - sets react MapBox map to all map containers
  // ------------------------------------------------------------------

  setMap() {
    // Hide all MapContainers on page
    const allMapContainers = document.querySelectorAll(
      ".map-container",
    ) as unknown as HTMLElement[];

    allMapContainers.forEach((container) => {
      const mapContainerParent = container.parentElement;
      if (mapContainerParent) {
        mapContainerParent.style.display = "none";
      }
    });

    if (mapContainer) {
      // Show active MapContainer on page (the one found by getElementById)
      const mapContainerParent = mapContainer.parentElement;
      if (mapContainerParent) {
        mapContainerParent.style.display = "block";
      }

      // Check data tag if the MapBlock is set to show start (on page load)
      MapProps.show =
        mapContainer?.dataset.showStart?.toLocaleLowerCase() === "true"
          ? true
          : false;

      if (hero?.contains(mapContainer)) {
        // if the mapContainer is Hero

        if (MapProps.show) {
          hero?.classList.add("map");
          mapButton?.classList.remove("show-map");
          mapButton.classList.add("hide-map");
          this.showMap();
        }

        // event for map toggle button in the hero element
        mapButton?.addEventListener("click", (e: Event) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (!MapProps.show) {
            hero?.classList.add("map");
            mapButton.classList.add("hide-map");
            mapButton?.classList.remove("show-map");
            this.showMap();
          } else {
            hero?.classList.remove("map");
            mapButton.classList.add("show-map");
            mapButton?.classList.remove("hide-map");
            this.hideMap();
          }
        });

        topNavigationHeight = topNavigation?.offsetHeight;

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            topNavigationHeight = topNavigation?.offsetHeight;
            if (mapContainer && topNavigationHeight) {
              mapContainer.style.marginTop = `${topNavigationHeight}px`;
              mapContainer.style.height = `${632 - topNavigationHeight}px`;
            }
          }
        });
        if (topNavigation) {
          resizeObserver.observe(topNavigation);
        }

        if (mapContainer && topNavigationHeight) {
          mapContainer.style.marginTop = `${topNavigationHeight}px`;
          mapContainer.style.height = `${632 - topNavigationHeight}px`;
        }
      } else {
        // if the mapContainer is in ContentArea
        MapProps.show = true;
        this.showMap();
      }
    }
  }

  // ------------------------------------------------------------------
  //  showMap - shows the map by starting the react component
  // ------------------------------------------------------------------

  showMap() {
    topNavigation?.classList.add("fixed-small");
    MapProps.show = true;
    updateReactComponent(reactComponentIds.MAP);
  }

  // ------------------------------------------------------------------
  //  hideMap - hides map
  // ------------------------------------------------------------------

  hideMap() {
    topNavigation?.classList.remove("fixed-small");
    MapProps.show = false;
    updateReactComponent(reactComponentIds.MAP);
  }

  setExpandableBlock() {
    const components: NodeListOf<Element> =
      document.querySelectorAll(".expandable-block");

    components.forEach((element) => {
      const teaser = new ExpandableBlock(element);
    });
  }

  // -------------------------------------------------------------------------------------------
  //  setScrollTrigger
  //  - adds IntersectionObserver for all elements with the class .scroll-trigger
  //    It handles Lazy loading and triggers component events when visible in viewport
  // -------------------------------------------------------------------------------------------

  setScrollTrigger() {
    const scrollTrigger = (selector: string, options = {}) => {
      const els = [...document.querySelectorAll(selector)];

      els.forEach((el) => {
        scrollTriggerObserver(el, options);
        el.classList.remove("scroll-trigger");
      });
    };

    const scrollTriggerObserver = (
      el: Element,
      options: IntersectionObserverInit,
    ) => {
      const deviceType = checkDeviceType();

      let observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible-on-screen");

            // Animation triggers
            if (entry.target.classList.contains("quick-facts")) {
              quickFacts.animateResult(entry.target as HTMLElement);
            }

            if (entry.target.classList.contains("value-proposition")) {
              const swiperData = this.swiperElementsData.find(
                (data) => data.element.id === entry.target.id,
              );
              if (swiperData) {
                valueProposition.startAutoPlay(
                  entry.target as HTMLElement,
                  swiperData,
                );
              }
            }

            // Lazy loading of images
            if (entry.target.classList.contains("teaser")) {
              if (deviceType !== devices.desktop) {
                teaser.loadImage(entry.target as HTMLElement);
              }
            }

            if (entry.target.classList.contains("link-block")) {
              if (deviceType !== devices.desktop) {
                linkBlock.loadImage(entry.target as HTMLElement);
              }

              linkBlock.show(entry.target as HTMLElement);
            }

            observer.unobserve(entry.target);
          }
        });
      }, options);
      observer.observe(el);
    };

    scrollTrigger(".scroll-trigger", {
      root: null,
      rootMargin: "-20% 0% -20% 0%",
      threshold: 0,
    });
  }

  // ------------------------------------------------------------------
  //  setResizeObserver - checks when viewport is resizing
  // ------------------------------------------------------------------

  setResizeObserver() {
    if (topBanner && pageContent) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          this.setTopBannerHeight(entry.target);
          this.setMap();
        }
      });
      resizeObserver.observe(topBanner);
    }
  }

  // ------------------------------------------------------------------
  //  setTopBannerHeight - sets the page top based on topbanner height
  // ------------------------------------------------------------------

  setTopBannerHeight(element: Element) {
    const topBannerHeight = element.clientHeight;
    pageContent.setAttribute("style", `margin-top:${topBannerHeight}px`);
  }
}

export const ghab = new GHAB();
